import * as RadixPopover from '@radix-ui/react-popover';
import { type PopoverPortalProps } from '@radix-ui/react-popover';
import { memo, type ReactNode } from 'react';

import { usePortalStore } from '@air/primitive-portal';
import { TooltipContent, TooltipRoot, TooltipTrigger } from '@air/primitive-tooltip';
import { tailwindVariants } from '@air/utils-tailwind';

const popover = tailwindVariants({
  base: 'z-portal max-h-[calc(var(--radix-popover-content-available-height)-32px)] w-full radix-side-bottom:animate-slide-up radix-side-top:animate-slide-down overflow-y-auto rounded-4 ads:border-default ads:bg-raised bg-surface-1 shadow-popover outline-none dark:border dark:border-gray-5',
  defaultVariants: {
    size: 'small',
  },
  variants: {
    size: {
      large: 'min-w-[240px] p-2',
      small: 'min-w-[216px] p-1.5',
    },
  },
});

export type PopoverSize = 'small' | 'large';

export type PopoverProps = RadixPopover.PopoverProps &
  RadixPopover.PopoverContentProps & {
    size?: PopoverSize;
    trigger: RadixPopover.PopoverTriggerProps['children'];
    container?: PopoverPortalProps['container'];
    /**
     * When set, the trigger also shows a tooltip with this content. Composed so
     * the same element is both the tooltip and popover trigger.
     */
    triggerTooltip?: ReactNode;
  };

export const Popover = memo(
  ({
    align = 'start',
    children,
    className,
    collisionPadding = 8,
    container,
    defaultOpen,
    modal,
    onOpenChange,
    open,
    side = 'bottom',
    sideOffset = 8,
    size = 'small',
    trigger,
    triggerTooltip,
    ...restOfProps
  }: PopoverProps) => {
    const portal = usePortalStore((state) => state.portal);
    // A controlled popover with no onOpenChange cannot close. Escape still calls
    // preventDefault, and other handlers must be allowed to run.
    const dismissesOnEscape = open === undefined || onOpenChange !== undefined;

    const root = (
      <RadixPopover.Root defaultOpen={defaultOpen} modal={modal} onOpenChange={onOpenChange} open={open}>
        {/* Nest the tooltip trigger under the popover trigger so both attach to the same element. */}
        <RadixPopover.Trigger asChild>
          {triggerTooltip ? <TooltipTrigger asChild>{trigger}</TooltipTrigger> : trigger}
        </RadixPopover.Trigger>
        <RadixPopover.Portal container={container || portal}>
          <RadixPopover.Content
            align={align}
            className={popover({ class: className, size })}
            collisionPadding={collisionPadding}
            side={side}
            sideOffset={sideOffset}
            {...restOfProps}
            data-dismisses-on-escape={dismissesOnEscape ? 'true' : undefined}
          >
            {children}
          </RadixPopover.Content>
        </RadixPopover.Portal>
      </RadixPopover.Root>
    );

    if (!triggerTooltip) {
      return root;
    }

    return (
      <TooltipRoot>
        {root}
        <TooltipContent>{triggerTooltip}</TooltipContent>
      </TooltipRoot>
    );
  },
);

Popover.displayName = 'Popover';
